/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { omit, isEqualWith } from "lodash"
import { useTranslationsMap } from "common/utils/i18n"
import notification from "components/common/notification"
import { EventAction, EventCategory, EventEntryPoint } from "constants/tracking"
import useInventoryContext from "hooks/useInventoryContext"
import { useMenuItemSearch } from "hooks/useMenuItemSearch"
import { FC, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useLocation } from "react-router-dom"
import { arrayMove, SortEndHandler } from "react-sortable-hoc"
import { AnyAction } from "redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import {
  AxiosAction,
  CreateItemRequest,
  ResponseWithError,
  UpdateItemRequest,
} from "types/api"
import { Item, SellingTime } from "types/models/menu"
import { currencyToMinorUnit } from "utils/currency"
import { formValuesToWeight } from "utils/form_helper"
import {
  getLinkedModifierGroups,
  itemToUpdateItemRequest,
  useConfirmDeleteItemOptions,
  itemErrorTargets,
  useItemErrorResponseToMessage,
} from "utils/item_helper"
import { getCreateAndDeleteAttributesField } from "utils/attribute_helper"
import { errorTarget } from "utils/modifier_group_helper"
import { storageHelper } from "utils/storage"
import { getResponseErrorEventLabels, TroyWebTracker } from "utils/tracking"
import {
  getSellingTimeByID,
  getSellingTimeMetadata,
} from "utils/selling_time_helper"
import { getCurrencyCodeByCountryCode } from "common/utils/locale"
import { getUserCountry } from "troy-common/utils"
import { commonErrorCallback } from "utils/misc"
import { useIsAllowSaleAlcohol } from "pages/menu/detail/hooks/useIsAllowSaleAlcohol"
import { useIsAllowSaleTobacco } from "pages/menu/detail/hooks/useIsAllowSaleTobacco"
import { ItemValidationRequest } from "types/api/menuUpdateItem.type"
import { GetAlcoholPredictionResponse } from "types/api/menuGetAlcoholPrediction.type"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { useSellingTimes } from "hooks/useSellingTimes"
import { partialMenuApi, PARTIAL_MENU_REFRESH_TAGS } from "queries"
import { ItemDrawer } from ".."
import { OnSaveParams } from "../ItemDrawer/ItemDrawer"
import {
  ALCOHOL,
  InvalidOperationOnAlcoholItem,
  InvalidOperationOnTobaccoItem,
  TOBACCO,
} from "../ItemDrawer/constant"

export interface MenuItemDrawerProps {
  visible: boolean
  onClose(e: any): void
}

export const MenuItemDrawer: FC<MenuItemDrawerProps> = ({
  visible,
  onClose,
}: MenuItemDrawerProps) => {
  const dispatch = useDispatch()

  const location = useLocation()

  const history = useHistory()

  const itemValidationRef = useRef<Item>()

  const { sellingTimes } = useSellingTimes()

  const {
    merchantID,
    outlet,
    outletV2,
    menu: { modifierGroups },
    drawer: {
      item,
      sourceShowItem,
      linkedModifierGroupsDrawerState,
      showUploadPhoto,
    },
    isMartDepartmentStructure,
    isSellingTimeStructure,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    enableTroyNewMerchantEndpoint,
    alcoholPrediction,
    tobaccoPrediction,
  } = useSelector((state: AppState) => state?.menu)

  const isAllowSaleAlcohol = useIsAllowSaleAlcohol()
  const isAllowSaleTobacco = useIsAllowSaleTobacco()
  const enablePartialMenu = useEnablePartialMenu()

  const { mustEditThroughMenu } = useInventoryContext()
  const { refetchSearch } = useMenuItemSearch()

  const currencyCode =
    (enableTroyNewMerchantEndpoint
      ? outletV2?.currency?.code
      : outlet?.currency) || getCurrencyCodeByCountryCode(getUserCountry())
  const troyEventCategory =
    sourceShowItem === EventCategory.menuOverviewModifyItem
      ? EventCategory.menuOverviewModifyItem
      : EventCategory.menuItemsModifyitem

  const {
    linkedModifierGroups,
    hasChanged: hasLinkedModifierGroupsChanged,
    hasBeenRearranged: hasLinkedModifierGroupsRearranged,
  } = linkedModifierGroupsDrawerState ?? {}

  const isEdit = !!item?.itemID

  const trackingLabels: any = {
    ...(merchantID
      ? { STORE_ID: merchantID }
      : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
    ITEM_ID: item?.itemID ?? "",
    INTENT: isEdit ? "EDIT_ITEM" : "ADD_ITEM",
    IS_PARTIAL_MENU: enablePartialMenu,
  }

  const [isItemEditDisabled, setIsItemEditDisabled] = useState(false)

  const handleMount = () => {
    // validate fields on first drawer open
    setIsItemEditDisabled(
      !isEdit
        ? false
        : storageHelper.isItemEditDisabled(item?.itemID ?? "") ?? false,
    )
    itemValidationRef.current = undefined
  }

  useEffect(handleMount, [])

  const errorTextMap = useTranslationsMap({
    daily: "menu.item.alerts.reachEditLimitDaily.title",
    weekly: "menu.item.alerts.reachEditLimitWeekly.title",
    monthly: "menu.item.alerts.reachEditLimitMonthly.title",
    notAllowSaleAlcohol: "alcoholSetting.notAllowedSellAcohol",
    notAllowSaleTobacco: "tobaccoSetting.notAllowedSellTobacco",
  })

  const textMap = useTranslationsMap({
    createSuccess: "menu.notifications.createItem.successMessage",
    editSuccess: "menu.notifications.editItem.successMessage",
    createError: "menu.notifications.createItem.errorMessage",
    deleteSuccess: "menu.notifications.deleteItem.successMessage",
    modifierGroups: "menu.drawers.item.modifierGroups",
    deleteItem: "menu.drawers.item.buttons.deleteItem",
    addPhoto: "menu.item.modal.photo.addPhoto",
    errorRequired: "menu.errors.requiredField",
    errorDecimals: "menu.errors.number.decimals",
    errorPriceExceedLimit: "menu.errors.price.priceExceedLimit",
    changeDailyLimit: "menu.errors.changeDailyLimit.title",
    modifierGroupPickUpTo: "menu.item.modifierGroup.pickUpTo",
    pickExactly: "menu.item.modifierGroup.pick",
    pickXtoY: "menu.item.modifierGroup.pickXtoY",
    noDataAvailable: "menu.item.modifierGroup.noDataAvailable",
    editPhoto: "menu.item.modal.title.editPhoto",
    labelCategory: "menu.items.headings.category",
    labelSubCategory: "catalogue.item.labels.subCategory",
    labelPrice: "menu.drawers.item.labels.price",
    edit: "menu.actions.edit",
    linkItemLimit: "menu.errors.linkItemLimit",
    itemNamePlaceholder: "menu.drawers.item.placeholders.name",
    itemCode: "catalogue.item.labels.itemCode",
    itemCodePlaceholder: "catalogue.item.placeholders.itemCode",
    skuID: "catalogue.item.labels.skuID",
    skuIDPlaceholder: "catalogue.item.placeholders.skuID",
    skuIDTooltip: "catalogue.item.tooltips.skuID",
    errorBarcodeExceedDigits: "catalogue.errors.barcodeExceedDigits",
    errorBarcodeNumeric: "catalogue.errors.barcodeNumeric",
    linkOptGroupHint: "menu.drawers.item.linkOptionGroupHint",
    linkOptGroupDepartmentHint:
      "menu.drawers.item.linkOptionGroupDepartmentHint",
    linkOptGroupBtn: "menu.drawers.item.buttons.linkModifierGroups",
    uploadFailed: "menu.errors.uploadFailed",
    addItem: "menu.drawers.item.buttons.addItem",
    saveChanges: "menu.buttons.saveChanges",
    saveChangesEverywhereTitle:
      "menu.drawers.item.menuGroup.saveChanges.everywhere.title",
    saveChangesEverywhereDescription:
      "menu.drawers.item.menuGroup.saveChanges.everywhere.description",
    saveChangesSelectedTitle:
      "menu.drawers.item.menuGroup.saveChanges.selected.title",
    saveChangesSelectedDescription:
      "menu.drawers.item.menuGroup.saveChanges.selected.description",
  })

  const handleSortModifierGroup: SortEndHandler = ({ oldIndex, newIndex }) => {
    if (linkedModifierGroups && oldIndex !== newIndex) {
      dispatch(
        actionCreator.menu.rearrangeLinkedModifierGroup(
          arrayMove(linkedModifierGroups, oldIndex, newIndex),
        ),
      )
    }
  }

  const itemErrorResponseToMessage = useItemErrorResponseToMessage()

  const errorCallback = (response: ResponseWithError) => {
    dispatch(actionCreator.global.hideLoading())

    const { message = "", target = "" } = response ?? {}

    if (target === itemErrorTargets.ExceedChangeLimit) {
      const [range, limit, date] = message.split(":")

      storageHelper.setItemEditDisabled({
        itemID: item?.itemID ?? "",
        date,
        limit,
        range,
      })

      setIsItemEditDisabled(true)

      return errorTextMap[range]
    }

    return itemErrorResponseToMessage(response)
  }

  const linkModifierGroupsToItem = async (itemID: string) => {
    let success = true
    let labels: any = {
      ITEM_ID: itemID,
      MODIFIER_GROUP_ID: linkedModifierGroups?.map(
        value => value.modifierGroupID,
      ),
      ...(merchantID
        ? { STORE_ID: merchantID }
        : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
    }
    if (hasLinkedModifierGroupsChanged) {
      // unlink the mod groups if exist
      // When enablePartialMenu is true, Redux modifierGroups is empty, so use item.linkedModifierGroupIDs directly
      const currentlyLinkedModifierGroupIDs = enablePartialMenu
        ? item?.linkedModifierGroupIDs ?? []
        : getLinkedModifierGroups(modifierGroups, itemID).map(
            mg => mg.modifierGroupID,
          )

      await Promise.all(
        currentlyLinkedModifierGroupIDs.map(async modifierGroupID => {
          const unlinkResp = await dispatch(
            actionCreator.modifierGroup.UnLinkItemFromModifierGroup(
              modifierGroupID,
              itemID,
              merchantID,
              merchantGroupID,
              menuGroupID,
              errorCallback,
            ),
          )
          if (unlinkResp.type.endsWith("_FAIL")) {
            success = false
          }
        }),
      )
      if (!success) {
        return success
      }
      // link the mod groups
      if (linkedModifierGroups) {
        await Promise.all(
          linkedModifierGroups.map(async mg => {
            const linkItemResp = await dispatch(
              actionCreator.modifierGroup.linkItemToModifierGroup(
                mg.modifierGroupID,
                itemID,
                merchantID,
                merchantGroupID,
                menuGroupID,
                (errResp: ResponseWithError) => {
                  const target = errResp?.target
                  const message = errResp?.message
                  if (
                    target === errorTarget.exceedMaxModifierGroupPerItemLimit
                  ) {
                    return textMap.linkItemLimit.replace(
                      /\$count/i,
                      message || "",
                    )
                  }
                  return errorCallback(errResp)
                },
              ),
            )
            if (linkItemResp.type.endsWith("_FAIL")) {
              success = false

              labels = {
                STATUS: "FAILURE",
                ...getResponseErrorEventLabels(
                  (linkItemResp as any)?.error?.request?.response,
                ),
              }
            } else {
              labels = {
                STATUS: "SUCCESS",
              }
            }
          }),
        )
      }
    }
    TroyWebTracker.sendEvent({
      category: troyEventCategory,
      action: EventAction.submitModifyModifiergroup,
      labels: {
        ...labels,
        TRAFFIC_MEDIUM: showUploadPhoto ? "MISSING_PHOTO" : "",
      },
    })
    return success
  }

  const rearrangeModifierGroups = async (itemID: string) => {
    let success = true
    let labels: any = {
      ITEM_ID: itemID,
      ...(merchantID
        ? { STORE_ID: merchantID }
        : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
    }
    if (linkedModifierGroups && hasLinkedModifierGroupsRearranged) {
      const resp: any = await dispatch(
        actionCreator.menu.sortMenuEntities({
          merchantID,
          parentEntityID: itemID,
          parentEntityType: 5,
          sorts: linkedModifierGroups.map((modifierGroup, i) => ({
            resourceID: modifierGroup.modifierGroupID,
            sortOrder: i,
          })),
          sortToEntityType: 6,
          merchantGroupID,
          menuGroupID,
        }),
      )
      if (resp.type.endsWith("_FAIL")) {
        success = false

        labels = {
          STATUS: "FAILURE",
          ...getResponseErrorEventLabels(
            (resp as any)?.error?.request?.response,
          ),
        }
      } else {
        labels = {
          STATUS: "SUCCESS",
        }
      }
      TroyWebTracker.sendEvent({
        category: troyEventCategory,
        action: EventAction.rearrangeModifier,
        labels,
      })
    }

    return success
  }

  const handleFinishSubmission = async (itemID: string) => {
    dispatch(actionCreator.global.hideLoading())

    if (enablePartialMenu) {
      // Invalidate RTK Query cache for partial menu to trigger refetch
      dispatch(partialMenuApi.util.invalidateTags(PARTIAL_MENU_REFRESH_TAGS))
    } else {
      // refetch menu from Redux
      await dispatch(
        actionCreator.menu[
          isMartDepartmentStructure ? "fetchCatalogue" : "fetchMenu"
        ]({
          merchantID,
          isSellingTimeStructure,
          merchantGroupID,
          menuGroupID,
        }),
      )
    }

    await refetchSearch()

    dispatch(actionCreator.menu.selectItemByID(itemID))

    dispatch(
      actionCreator.menu.toggleDrawer({
        showItem: false,
        showLinkModifierGroup: false,
      }),
    )
  }

  const saveErrorCallback = (response: ResponseWithError) => {
    dispatch(actionCreator.global.hideLoading())
    return errorCallback(response)
  }

  const getSellingTimeInfoEventLabels = (selectedSellingTime?: SellingTime) => {
    if (isSellingTimeStructure) {
      const previousSellingTime = isEdit
        ? getSellingTimeByID(item?.sellingTimeID, sellingTimes)
        : sellingTimes[0]
      const newSellingTime = selectedSellingTime
      if (!previousSellingTime || !newSellingTime) return {}
      return {
        PREVIOUS_SELLING_TIME_INFO: getSellingTimeMetadata(previousSellingTime),
        SELLING_TIME_INFO: getSellingTimeMetadata(newSellingTime),
      }
    }
    return {}
  }

  const attributeItemErrorCallback = (response: ResponseWithError) => {
    if (response?.target === InvalidOperationOnAlcoholItem) {
      return errorTextMap.notAllowSaleAlcohol
    }
    if (response?.target === InvalidOperationOnTobaccoItem) {
      return errorTextMap.notAllowSaleTobacco
    }
    return true
  }

  const upsertItemV2ErrorCallback = (response: ResponseWithError) => {
    const attrCallback = attributeItemErrorCallback(response)
    if (attrCallback === true) {
      return saveErrorCallback(response)
    }
    return attrCallback
  }

  const handleSaveCreateV2 = async (
    data: CreateItemRequest,
    callback?: (newItemID: string) => void,
    selectedSellingTime?: SellingTime,
    previousItemData?: Item | null,
    currentItemData?: Item,
  ) => {
    let hasSubmitError: boolean = false
    let labels: any = {
      ...trackingLabels,
      PREVIOUS_ITEM_INFO: previousItemData || null,
      ITEM_INFO: currentItemData,
      ...getSellingTimeInfoEventLabels(selectedSellingTime),
      STATUS: "SUCCESS",
      MME_ITEM_ENDPOINT_REDESIGN: true,
    }

    const resp: AxiosAction<any> = await dispatch(
      actionCreator.menu.upsertItemV2({
        merchantID,
        data: {
          ...data,
          item: omitAttributeDataFromItem(data.item),
          newAttributes: data.item?.attributes,
        },
        merchantGroupID,
        menuGroupID,
        errorCallback: upsertItemV2ErrorCallback,
        closeDrawers: true,
        showGlobalLoading: false,
      }) as any,
    )

    if (!resp.error) {
      const itemID = resp?.payload?.data?.itemID ?? ""
      callback?.(itemID)

      // after getting the newly-created item ID, we link the mod groups to it
      const linkItemToModifierGroupFullSuccess = await linkModifierGroupsToItem(
        itemID,
      )
      const rearrangeModifierGroupsSuccess = await rearrangeModifierGroups(
        itemID,
      )

      hasSubmitError =
        hasSubmitError ||
        !linkItemToModifierGroupFullSuccess ||
        !rearrangeModifierGroupsSuccess
    } else {
      labels = {
        ...labels,
        STATUS: "FAILURE",
        ...getResponseErrorEventLabels(resp),
      }
      hasSubmitError = true
    }

    TroyWebTracker.sendEvent({
      category: troyEventCategory,
      action: EventAction.submitModifyItem,
      labels,
    })

    trackItemValidationDifference(data.item)

    return hasSubmitError
  }

  const handleSaveEditV2 = async (
    itemID: string,
    data: UpdateItemRequest,
    selectedSellingTime?: SellingTime,
    previousItemData?: Item | null,
    currentItemData?: Item,
  ) => {
    const clickSaveTimestamp = new Date().valueOf()
    let saveShownTimestamp: number | null = null
    let hasSubmitError: boolean = false
    let labels = {
      ...trackingLabels,
      PREVIOUS_ITEM_INFO: previousItemData || null,
      ITEM_INFO: currentItemData,
      ...getSellingTimeInfoEventLabels(selectedSellingTime),
      STATUS: "SUCCESS",
      MME_ITEM_ENDPOINT_REDESIGN: true,
    }

    // do both concurrently, as we already have the itemID
    const [resp, linkItemToModifierGroupFullSuccess] = await Promise.all([
      dispatch(
        actionCreator.menu.upsertItemV2({
          merchantID,
          data: { ...data, item: omitAttributeDataFromItem(data.item) },
          merchantGroupID,
          menuGroupID,
          errorCallback: upsertItemV2ErrorCallback,
          closeDrawers: false,
          showGlobalLoading: false,
        }) as any,
      ) as AxiosAction<any>,
      linkModifierGroupsToItem(itemID),
    ])
    const rearrangeModifierGroupsSuccess = await rearrangeModifierGroups(itemID)

    hasSubmitError =
      hasSubmitError ||
      !linkItemToModifierGroupFullSuccess ||
      !rearrangeModifierGroupsSuccess

    if (!resp.error) {
      saveShownTimestamp = new Date().valueOf()
    } else {
      labels = {
        ...labels,
        STATUS: "FAILURE",
        ...getResponseErrorEventLabels(resp),
      }
      hasSubmitError = true
    }

    if (TroyWebTracker.isMenuGroup) {
      labels = {
        ...labels,
        CLICK_SAVE_TIMESTAMP: clickSaveTimestamp,
        SAVE_SHOWN_TIMESTAMP: saveShownTimestamp,
      }
    }

    TroyWebTracker.sendEvent({
      category: troyEventCategory,
      action: EventAction.submitModifyItem,
      labels: {
        ...labels,
        TRAFFIC_MEDIUM: showUploadPhoto ? "MISSING_PHOTO" : "",
      },
    })

    trackItemValidationDifference(data.item)

    return hasSubmitError
  }

  const omitAttributeDataFromItem = (dataItem: Item) => {
    return omit(dataItem, ["attributes", "attributeForm"])
  }

  const trackItemValidationDifferenceCustomizer = (
    value: any,
    othValue: any,
    objKey: any,
  ) => {
    // itemClassID can have "" or undefined value, but both of them can be considered the same
    if (objKey === "itemClassID" && !value && !othValue) {
      return true
    }
    return undefined
  }

  // Set it as async so not blocking other flow
  const trackItemValidationDifference = async (dataItem: Item) => {
    let validatedItem = itemValidationRef.current
    if (!validatedItem || validatedItem.itemID !== dataItem.itemID) {
      return
    }

    validatedItem = omitAttributeDataFromItem(validatedItem)
    const updatedItem = omitAttributeDataFromItem(dataItem)
    if (
      isEqualWith(
        validatedItem,
        updatedItem,
        trackItemValidationDifferenceCustomizer,
      )
    ) {
      return
    }

    TroyWebTracker.sendEvent({
      category: troyEventCategory,
      action: EventAction.itemValidationCompare,
      labels: {
        ...trackingLabels,
        VALIDATED_ITEM_INFO: validatedItem,
        UPDATED_ITEM_INFO: updatedItem,
        MME_ITEM_ENDPOINT_REDESIGN: true,
      },
    })
  }

  const handleSaveItemValidation = async (data: ItemValidationRequest) => {
    let labels = {
      ...trackingLabels,
      ITEM_INFO: data.item,
      STATUS: "SUCCESS",
      MME_ITEM_ENDPOINT_REDESIGN: true,
    }

    const res: AxiosAction<any> = await dispatch(
      actionCreator.menu.itemValidation({
        merchantID,
        data: { ...data, item: omitAttributeDataFromItem(data.item) },
        merchantGroupID,
        menuGroupID,
        errorCallback: saveErrorCallback,
      }) as any,
    )

    if (res.error) {
      labels = {
        ...labels,
        STATUS: "FAILURE",
        ...getResponseErrorEventLabels(res),
      }
    }

    TroyWebTracker.sendEvent({
      category: troyEventCategory,
      action: EventAction.itemValidation,
      labels,
    })

    itemValidationRef.current = data.item

    return !!res.error
  }

  const determineSpecialItemType = (
    alcoholFormValue: boolean,
    tobaccoFormValue: boolean,
  ): string | undefined => {
    if (isAllowSaleAlcohol && alcoholFormValue) {
      return ALCOHOL
    }
    if (isAllowSaleTobacco && tobaccoFormValue) {
      return TOBACCO
    }
    return ""
  }

  const determinePrediction = (
    overridePrediction?: GetAlcoholPredictionResponse["predictions"],
  ): {
    isAlcohol?: boolean
    isTobacco?: boolean
  } => {
    const prediction: { isAlcohol?: boolean; isTobacco?: boolean } = {}
    if (isAllowSaleAlcohol) {
      prediction.isAlcohol = overridePrediction?.isAlcohol ?? alcoholPrediction
    }
    if (isAllowSaleTobacco) {
      prediction.isTobacco = overridePrediction?.isTobacco ?? tobaccoPrediction
    }
    return prediction
  }

  const handleSave = ({
    form,
    priceValidator,
    uploadedImg,
    uploadedImages,
    hasItemChanged,
    otherItemFields,
    attributes,
    selectedSellingTime,
    newAttributes,
    translation,
    isItemValidation,
    successCallback,
  }: OnSaveParams) => {
    form.validateFields(
      async (
        formError: any,
        { alcoholTagging, tobaccoTagging, ...formValues }: any,
      ) => {
        let hasSubmitError: boolean = false

        let itemID = item?.itemID ?? ""
        if (hasItemChanged) {
          // handle create or edit
          if (formError) return
          if (
            !priceValidator.validateOnSave(formValues.priceInMin, "priceInMin")
          ) {
            return
          }
          dispatch(actionCreator.global.showLoading())

          const isAiGeneratedItemDescription = (
            item?.aiGeneratedFields || []
          ).includes("description")

          const didUpdateItemDescription =
            (formValues.description || "") !== (item?.description ?? "")

          const specialItemType = determineSpecialItemType(
            alcoholTagging,
            tobaccoTagging,
          )

          const prediction = determinePrediction(otherItemFields.prediction)

          const itemData: Item = {
            ...item,
            ...formValues,
            ...otherItemFields,
            attributes,
            imageURL: uploadedImg,
            imageURLs: uploadedImages,
            priceInMin: currencyToMinorUnit(
              formValues.priceInMin,
              currencyCode,
            ),
            weight: formValuesToWeight(formValues.weight),
            sellingTimeID: selectedSellingTime?.sellingTimeID,
            nameTranslation: translation?.nameTranslation,
            descriptionTranslation: translation?.descriptionTranslation,
            ...((isAllowSaleAlcohol || isAllowSaleTobacco) && {
              specialItemType,
              prediction,
            }),
            ...(isAiGeneratedItemDescription && didUpdateItemDescription
              ? {
                  aiGeneratedFields: (item?.aiGeneratedFields || []).filter(
                    x => x !== "description",
                  ),
                }
              : {}),
          }

          const data = {
            ...itemToUpdateItemRequest(itemData),
            newAttributes,
            ...(isEdit
              ? getCreateAndDeleteAttributesField(
                  item?.attributes ?? [],
                  attributes ?? [],
                )
              : {}),
          }

          const previousItemData = isEdit
            ? item
            : {
                ...item,
                sellingTimeID: selectedSellingTime?.sellingTimeID,
              }
          const currentItemData = itemData

          if (isItemValidation) {
            // handle item validation
            hasSubmitError = await handleSaveItemValidation({
              item: data.item,
              categoryID: data.categoryID,
            })
          } else if (isEdit) {
            // handle edit
            hasSubmitError = await handleSaveEditV2(
              itemID,
              {
                ...data,
                itemID,
                itemIDs: [itemID],
                availableStatus: item?.availableStatus ?? 0,
              },
              selectedSellingTime,
              previousItemData,
              currentItemData,
            )
          } else {
            // handle create
            hasSubmitError = await handleSaveCreateV2(
              data,
              (newItemID: string) => {
                itemID = newItemID
              },
              selectedSellingTime,
              previousItemData,
              currentItemData,
            )
          }
        } else if (
          hasLinkedModifierGroupsChanged ||
          hasLinkedModifierGroupsRearranged
        ) {
          dispatch(actionCreator.global.showLoading())
          if (hasLinkedModifierGroupsChanged) {
            // handle linking modifiers
            const linkModifierGroupsToItemError = !(await linkModifierGroupsToItem(
              itemID,
            ))
            hasSubmitError = hasSubmitError || linkModifierGroupsToItemError
          }
          if (hasLinkedModifierGroupsRearranged) {
            // handle rearranging modifiers
            const rearrangeModifierGroupsError = !(await rearrangeModifierGroups(
              itemID,
            ))
            hasSubmitError = hasSubmitError || rearrangeModifierGroupsError
          }
        }

        if (isItemValidation) {
          await handleItemValidationCallback({
            success: !hasSubmitError,
            successCallback,
          })
          return
        }

        if (!hasSubmitError) {
          notification.success({
            message: isEdit ? textMap.editSuccess : textMap.createSuccess,
          })
          handleFinishSubmission(itemID)

          // remove deeplink url after success edit
          if (isEdit) {
            history.replace(
              location.pathname.substring(
                0,
                location.pathname.lastIndexOf("/"),
              ),
            )
          }
        } else {
          dispatch(
            actionCreator.menu.toggleDrawer({
              showItem: false,
              showLinkModifierGroup: false,
            }),
          )
          dispatch(actionCreator.global.hideLoading())
        }
      },
    )
  }

  const handleItemValidationCallback = async ({
    success,
    successCallback,
  }: {
    success: boolean
    successCallback?: () => Promise<void>
  }) => {
    if (success) {
      await successCallback?.()
    }

    dispatch(actionCreator.global.hideLoading())
  }

  const handleSaveStockManagement = (
    form: WrappedFormUtils,
    showSuccess?: boolean,
  ) => {
    form.validateFields(async (formError: any, formValues: any) => {
      const itemID = item?.itemID

      if (formError || !itemID) {
        return
      }

      const {
        enableIms,
        currentStock,
        enableRestock,
        restockSetting,
      } = formValues
      const { freq, localtime, byDay, maxStock } = restockSetting || {}

      const res = (await dispatch(
        actionCreator.menu.upsertItemStock({
          itemID,
          merchantID,
          itemStock: {
            enableIms: !!enableIms,
            currentStock: Number(currentStock ?? 0),
            enableRestock: !!enableRestock,
            restockSetting: !enableRestock
              ? null
              : {
                  freq,
                  localtime,
                  byDay: byDay ?? null,
                  maxStock: Number(maxStock ?? 0),
                  nextRestockTime: "",
                },
          },
          errorCallback: commonErrorCallback,
        }) as AnyAction,
      )) as AxiosAction<{}>

      if (!res.error && showSuccess) {
        notification.success({
          message: textMap.editSuccess,
        })

        handleFinishSubmission(itemID)

        // remove deeplink url after success edit
        if (isEdit) {
          history.replace(
            location.pathname.substring(0, location.pathname.lastIndexOf("/")),
          )
        }
      }
    })
  }

  const confirmDeleteItemOptions = useConfirmDeleteItemOptions()

  const eventCategory = location.pathname.endsWith("/items")
    ? EventCategory.menuItemsTab
    : EventCategory.menuOverviewTab

  const handleDelete = async () => {
    const resp: any = await dispatch(
      actionCreator.menu.deleteItem({
        merchantID,
        itemID: item?.itemID ?? "",
        confirmOptions: confirmDeleteItemOptions,
        successMessage: textMap.deleteSuccess,
        showConfirm: false,
        errorCallback,
        merchantGroupID,
        menuGroupID,
      }),
    )
    let modifyItemLabels: any
    if (resp?.type?.endsWith("_SUCCESS")) {
      modifyItemLabels = {
        ...trackingLabels,
        STATUS: "SUCCESS",
      }
    } else if (resp?.type?.endsWith("_FAIL")) {
      modifyItemLabels = {
        ...trackingLabels,
        STATUS: "FAILURE",
        ...getResponseErrorEventLabels(resp),
      }
    }

    TroyWebTracker.sendEvent({
      category: troyEventCategory,
      action: EventAction.clickDeleteItem,
      labels: modifyItemLabels,
    })

    if (resp) {
      let labels = {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        ENTRY_POINT: EventEntryPoint.menuItemsModifyItem,
        ITEM_ID: item?.itemID,
        NO_ITEM_SELECTED: 1,
        STATUS: "SUCCESS",
        REASON: "",
        ERROR_MSG: "",
      }

      if (resp?.type.endsWith("_FAIL")) {
        labels = {
          ...labels,
          STATUS: "FAILURE",
          ...getResponseErrorEventLabels(resp),
        }
      }

      TroyWebTracker.sendEvent({
        category: eventCategory,
        action: EventAction.submitDeleteItem,
        labels,
      })
    }

    // Update Redux to remove deleted item from search results
    if (resp?.type?.endsWith("_SUCCESS")) {
      dispatch(
        actionCreator.menu.syncSearchItems(undefined, [item?.itemID ?? ""]),
      )
    }
  }

  return (
    <ItemDrawer
      type={mustEditThroughMenu ? "inventory" : "menu"}
      visible={visible}
      onClose={onClose}
      onSortModifierGroup={handleSortModifierGroup}
      onSave={handleSave}
      onDelete={handleDelete}
      isItemEditDisabled={isItemEditDisabled || mustEditThroughMenu}
      onSaveStockManagement={handleSaveStockManagement}
    />
  )
}
